import { ItemDaTabelaDePreco, ItemDaVenda, Loja, TabelaDePreco, TipoDeCliente } from '@/models'
import FindRegistroDeCustosUseCase from '../custos/FindRegistroDeCustosUseCase'
import { FindItemDaTabelaDePrecoUseCase } from './FindItemDaTabelaDePrecoUseCase'
import { displayNomeCompletoDoProduto } from '@/shareds/produto-shareds'
import moment from 'moment'

type UseCaseParams = {
	loja: Loja
	itens: ItemDaVenda[]
	tipoDeCliente: TipoDeCliente | string | null
	findRegistroDeCustosUseCase?: FindRegistroDeCustosUseCase
	findItemTabelaDePrecoUseCase?: FindItemDaTabelaDePrecoUseCase
}

export default async ({
	loja,
	itens,
	tipoDeCliente,
	findRegistroDeCustosUseCase = new FindRegistroDeCustosUseCase(),
	findItemTabelaDePrecoUseCase = new FindItemDaTabelaDePrecoUseCase(),
}: UseCaseParams): Promise<ItemDaVenda[]> => {
	const itensPromise = itens.map(async (item) => {
		if (!item.produto?.id) {
			throw new Error(`Produto ${displayNomeCompletoDoProduto(item.produto)} não cadastrado`)
		}

		let precoDeCusto = await findRegistroDeCustosUseCase.findPrecoDeCusto({
			lojaId: loja.id,
			produtoId: item.produto.id,
		})

		if (precoDeCusto === 0 && loja.configuracaoDaLoja.utilizarPrecoDeCompra) {
			const tipoDeClienteNormalizado =
        typeof tipoDeCliente === 'string' ? tipoDeCliente : tipoDeCliente?.nome

			const tabelaDoCliente = tipoDeClienteNormalizado
				? loja.configuracaoDaLoja.tabelasDePreco
					.find(
						(t) => t.tabelaDePreco.tipoDeCliente?.nome.toLocaleUpperCase() === tipoDeClienteNormalizado.toLocaleUpperCase() 
						&& !t.tabelaDePreco.isTabelaDePromocao,
					)
				?.tabelaDePreco
				: undefined

			const tabelaPadrao = loja.configuracaoDaLoja.tabelasDePreco.find((t) => !t.tipoDeCliente && !t.tabelaDePreco.isTabelaDePromocao)?.tabelaDePreco

			const tabelasDePromocao = ((): TabelaDePreco[] => {
				const base = tipoDeClienteNormalizado && tabelaDoCliente
					? loja.configuracaoDaLoja.tabelasDePreco.filter(
						({ tabelaDePreco }) =>
                tabelaDePreco.tipoDeCliente?.nome.toLocaleUpperCase() ===
                  tipoDeClienteNormalizado.toLocaleUpperCase() &&
                tabelaDePreco.isTabelaDePromocao,
					)
					: loja.configuracaoDaLoja.tabelasDePreco.filter(
						({ tabelaDePreco }) => !tabelaDePreco.tipoDeCliente && tabelaDePreco.isTabelaDePromocao,
					)

				return base
					.filter(({ tabelaDePreco }) =>
						moment().isSameOrAfter(moment(`${tabelaDePreco.inicioVigencia} 00:00:00`, 'YYYY-MM-DD HH:mm:ss')) &&
            moment().isSameOrBefore(moment(`${tabelaDePreco.fimVigencia} 23:59:59`, 'YYYY-MM-DD HH:mm:ss')),
					)
					.map(({ tabelaDePreco }) => tabelaDePreco)
			})()

			let itemDaTabelaDePreco: ItemDaTabelaDePreco | null = null

			if (tabelasDePromocao?.length) {
				const itensPromo = await Promise.all(
					tabelasDePromocao.map((tp) =>
						findItemTabelaDePrecoUseCase.getByIdDoProduto(tp.id, item.produto.id).catch(() => null),
					),
				)

				const itemComMenorPreco = itensPromo
					.filter((x): x is ItemDaTabelaDePreco => x !== null)
					.sort((a, b) => (a.precoCompra ?? 0) - (b.precoCompra ?? 0))

				itemDaTabelaDePreco = itemComMenorPreco[0] ?? null
			}

			if (!itemDaTabelaDePreco && tabelaDoCliente) {
				itemDaTabelaDePreco = await findItemTabelaDePrecoUseCase.getByIdDoProduto(tabelaDoCliente.id, item.produto.id).catch(() => null)
			}

			if (!itemDaTabelaDePreco && tabelaPadrao) {
				itemDaTabelaDePreco = await findItemTabelaDePrecoUseCase.getByIdDoProduto(tabelaPadrao.id, item.produto.id).catch(() => null)
			}

			if ( !itemDaTabelaDePreco || itemDaTabelaDePreco.precoCompra === 0 || !itemDaTabelaDePreco.precoCompra) 
				throw new Error("Produto sem preço de custo e preço de compra não cadastrado na tabela de preço.") 

			if (itemDaTabelaDePreco?.precoCompra) { 
				precoDeCusto = itemDaTabelaDePreco.precoCompra 
			}
		}

		const sku = item.produto.sku != null ? item.produto.sku : ""
		const lojaId = loja.id
		const precoReal = await findRegistroDeCustosUseCase.findPrecoDeCustoRealByLojaId(lojaId, sku)
		
		return aplicarPrecoNoItemDaVenda(item, precoDeCusto, precoReal)
	})

	return Promise.all(itensPromise)
}

function aplicarPrecoNoItemDaVenda(itemDaVenda: ItemDaVenda, precoDeCusto: number, precoReal: number): ItemDaVenda {
	return {
		...itemDaVenda,
		preco: precoDeCusto,
		precoDeCusto: precoDeCusto,
		desconto: {
			isPercentual: true,
			valor: 0,
		},
		percentualCashback: 0,
		diasParaEfetivarCashback: 0,
		diasParaExpirarCashback: 0,
		possuiDescontoPorTabela: false,
		precoDeCustoReal: precoReal,
	}
}
